import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';

export interface ApiResponse<T = any> {
    data: T;
    success: boolean;
    message?: string;
}

class ApiService {
    private axios: AxiosInstance;

    constructor() {
        this.axios = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            withCredentials: true, // Enable cookies for authentication
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        this.setupInterceptors();
    }

    private setupInterceptors() {
        // Request interceptor for logging and debugging
        this.axios.interceptors.request.use(
            (config) => {
                console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
                return config;
            },
            (error) => {
                console.error('❌ API Request Error:', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor for consistent error handling
        this.axios.interceptors.response.use(
            (response: AxiosResponse) => {
                console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
                return response;
            },
            (error) => {
                console.error(`❌ API Response Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network Error'}`);

                // Handle common error cases
                if (error.response?.status === 401) {
                    console.warn('🔒 Authentication required - redirecting to login');
                    // Could dispatch logout action here if using Redux/Zustand
                } else if (error.response?.status === 403) {
                    console.warn('🚫 Access forbidden');
                } else if (error.response?.status >= 500) {
                    console.error('💥 Server error');
                }

                return Promise.reject(error);
            }
        );
    }

    // Generic HTTP methods
    public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.get<T>(url, config);
        return response.data;
    }

    public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.post<T>(url, data, config);
        return response.data;
    }

    public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.put<T>(url, data, config);
        return response.data;
    }

    public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.patch<T>(url, data, config);
        return response.data;
    }

    public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.delete<T>(url, config);
        return response.data;
    }

    // File upload methods
    public async uploadFile<T = any>(url: string, file: File, additionalData?: Record<string, any>): Promise<T> {
        const formData = new FormData();
        formData.append('file', file);

        if (additionalData) {
            Object.entries(additionalData).forEach(([key, value]) => {
                formData.append(key, value);
            });
        }

        const response = await this.axios.post<T>(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    }

    public async uploadFormData<T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.post<T>(url, formData, {
            ...config,
            headers: {
                'Content-Type': 'multipart/form-data',
                ...config?.headers,
            },
        });

        return response.data;
    }
}

// Create singleton instance
export const apiService = new ApiService();